import React, { useEffect, useState } from 'react';
import { ContentContainer } from '../component';
import { observer } from 'mobx-react';
import {
  useAccountCreationStores,
  useCommonStores,
  useHistoryStore,
  useLegacyClients,
} from 'stores/useStores';
import {
  Content,
  Dropdown,
  Text,
  SurveyQuestion,
  Flex,
  Spinner,
  AnswerType,
} from 'component-library';
import { AccountCreationSteps, CmsRenderTree } from 'lib/interfaces';
import { useEffectOnce } from 'lib/helpers';
import {
  TaxTypesList,
  Months,
  IndustriesList,
  CompanyDataEnum,
  SurveyNameEnum,
  GraphCmsQuestionIdEnum,
  GraphCmsQuestionIdToAnswers,
  CreditClaimAnswers,
  ProgramNameEnum,
} from 'lib/constants';
import { useSurveyQuestions } from 'lib/useSurveyQuestions';
import renderTreeJson from '../../../../../hygraph/renderTree/unifiedAccountCreationExtensions.json';
import { companyFyeToTaxDeadline } from 'pages/dashboard/taxCredits/util';

const IndustriesListWithNameValue = IndustriesList.map((item) => {
  return { value: item, name: item };
});

const MonthsWithNameValue = Months.map((item) => {
  return { value: item, name: item };
});

const DropdownQuestionIdToAnswerOptions: {
  [key in CompanyDataEnum]?: { value: string; name: string }[];
} = {
  [CompanyDataEnum.INDUSTRY]: IndustriesListWithNameValue,
  [CompanyDataEnum.FISCAL_YEAR_END_DATE]: MonthsWithNameValue,
  [CompanyDataEnum.TAX_TYPE]: TaxTypesList,
};

interface TaxesStepProps {
  pathTo: string;
  pathFrom: string;
  onNext: () => void;
  onBack: () => void;
  taxYear: number;
}

export const TaxesStep = observer(
  ({ pathTo, pathFrom, onNext, onBack, taxYear }: TaxesStepProps) => {
    const { client } = useLegacyClients();
    const { accountCreation } = useAccountCreationStores();
    const { app, companyStore } = useCommonStores();
    const history = useHistoryStore();
    const {
      saveAnswers,
      isLoading,
      questionsToRender,
      addSurveyAnswers,
      surveyAnswers,
    } = useSurveyQuestions(
      SurveyNameEnum.UNIFIED_ACCOUNT_CREATION_EXTENSIONS,
      taxYear,
      {},
      renderTreeJson as CmsRenderTree,
    );

    const [shouldRenderTaxesQuestions, setShouldRenderTaxesQuestions] =
      useState(false);
    const [taxQuestions, setTaxQuestions] = useState({});

    useEffect(() => {
      const localSurveyAnswers: {
        [key in CompanyDataEnum]?: string;
      } = accountCreation.TaxesStepQuestions.reduce(
        (answers, item) => ({
          [item.id]: item.answer as string,
          ...answers,
        }),
        {},
      );

      let filingDateHasPassed = false;

      if (
        localSurveyAnswers[CompanyDataEnum.FISCAL_YEAR_END_DATE] &&
        localSurveyAnswers[CompanyDataEnum.TAX_TYPE]
      ) {
        const filingDate = companyFyeToTaxDeadline(
          taxYear,
          localSurveyAnswers[CompanyDataEnum.FISCAL_YEAR_END_DATE] as string,
          localSurveyAnswers[CompanyDataEnum.TAX_TYPE] as string,
        );
        filingDateHasPassed = filingDate < new Date();
      }

      setShouldRenderTaxesQuestions(filingDateHasPassed);
    }, [accountCreation.TaxesStepQuestions, taxQuestions, taxYear]);

    const onContinue = async () => {
      // set button loading
      accountCreation.isNextButtonLoading = true;

      const localSurveyAnswers: {
        [key in CompanyDataEnum]?: string;
      } = accountCreation.TaxesStepQuestions.reduce(
        (answers, item) => ({
          [item.id]: item.answer as string,
          ...answers,
        }),
        {},
      );

      // NOTE: Both of these calls update the company record. Resolving them
      // together can result in a race condition where one call overwrites the
      // other's changes. Therefore, we resolve them serially.
      await saveAnswers();
      await accountCreation.updateAccount(
        {
          industry: localSurveyAnswers[CompanyDataEnum.INDUSTRY],
          fiscalYearEndDate:
            localSurveyAnswers[CompanyDataEnum.FISCAL_YEAR_END_DATE],
          taxType: localSurveyAnswers[CompanyDataEnum.TAX_TYPE],
        },
        AccountCreationSteps.TAXES,
        true, // is unified account creation
      );

      // Look at the company's qualification questions to see if the company answered
      // an onboarding question that indicates the company signed up for a specific
      // year
      const filingForSpecificYearAnswer =
        surveyAnswers[GraphCmsQuestionIdEnum.CREDIT_CLAIM_YEAR];

      const filingForNextYearAnswer =
        GraphCmsQuestionIdToAnswers[GraphCmsQuestionIdEnum.CREDIT_CLAIM_YEAR][
          CreditClaimAnswers.NEXT_YEAR
        ];
      const filingForExtensionAnswer =
        GraphCmsQuestionIdToAnswers[GraphCmsQuestionIdEnum.CREDIT_CLAIM_YEAR][
          CreditClaimAnswers.EXTENSION
        ];
      const filingForDisasterExtensionAnswer =
        GraphCmsQuestionIdToAnswers[GraphCmsQuestionIdEnum.CREDIT_CLAIM_YEAR][
          CreditClaimAnswers.DISASTER_EXTENSION
        ];

      // If the customer indicated that they are filing for the next year,
      // create a new program for the next year. Otherwise, update with the
      // extension reason.
      // Note: This is separated from the other promises due to a race condition.
      if (filingForSpecificYearAnswer === filingForNextYearAnswer) {
        await client.CreateProgramV1({
          name: ProgramNameEnum.FED_RD_TAX,
          taxYear: taxYear + 1,
        });
      } else if (filingForSpecificYearAnswer === filingForExtensionAnswer) {
        await client.UpdatePrograms({
          programIdsToUpdate: companyStore.company.programs
            .filter((p) => p.taxYear === taxYear)
            .map((p) => p.id),
          fileTaxExtension: true,
          taxExtensionReason: 'standard extension',
        });
      } else if (
        filingForSpecificYearAnswer === filingForDisasterExtensionAnswer
      ) {
        await client.UpdatePrograms({
          programIdsToUpdate: companyStore.company.programs
            .filter((p) => p.taxYear === taxYear)
            .map((p) => p.id),
          fileTaxExtension: true,
          taxExtensionReason: 'disaster extension',
        });
      }

      if (!accountCreation.errorMsg) {
        history.push(pathTo);
        onNext();
      }
    };

    useEffectOnce(() => accountCreation.setTaxesStepQuestions());

    return (
      <ContentContainer
        onBack={onBack}
        onNext={onContinue}
        isNextButtonDisabled={
          accountCreation.enableNextButton(
            accountCreation.TaxesStepQuestions,
          ) ||
          (shouldRenderTaxesQuestions &&
            !questionsToRender['taxes']?.every(
              (question) => question.answerValue,
            ))
        }
        isNextLoading={accountCreation.isNextButtonLoading}
        pathFrom={pathFrom}
      >
        <Flex alignItems='start' direction='column' gap={8}>
          <Text
            text='Tell us about your taxes'
            variant='medium'
            size={app.isMobile ? 23 : 32}
          />
          <Text
            text='Your answers will be used to create a personalized MainStreet savings estimate.'
            size={15}
          />
        </Flex>
        {accountCreation.TaxesStepQuestions.map((item) => {
          return (
            <Content key={item.id} paddingLeftRight={0} paddingTopBottom={0}>
              <Text variant='medium' paddingBottom={8}>
                {item.question}
              </Text>
              <Dropdown
                dataTestId={`account_creation_${item.id}`}
                value={
                  item.answer
                    ? (DropdownQuestionIdToAnswerOptions[item.id] || []).find(
                        (title) => title.value === item.answer,
                      )?.name
                    : ''
                }
                options={DropdownQuestionIdToAnswerOptions[item.id] || []}
                placeholder=''
                onInputChange={(val) => {
                  if (typeof val !== 'string') {
                    accountCreation.setInputChange(
                      accountCreation.TaxesStepQuestions,
                      item.question,
                      val.value,
                    );
                    setTaxQuestions({
                      ...taxQuestions,
                      [item.id]: val.value,
                    });
                  }
                }}
                selectOnly
              />
            </Content>
          );
        })}
        {isLoading || !questionsToRender['taxes'] ? (
          <Flex justifyContent='center'>
            <Spinner />
          </Flex>
        ) : shouldRenderTaxesQuestions ? (
          <>
            {questionsToRender['taxes'].map((question) => (
              <SurveyQuestion
                key={question.id}
                placeholder={question.placeholder}
                text={question.text}
                subtitle={question.subtitle}
                questionTextSize={15}
                answerType={question.answerType as AnswerType}
                answerValue={question.answerValue}
                onChange={async (answer) => {
                  question.answerValue = answer;
                  addSurveyAnswers(question.id, 'taxes', [
                    { questionId: question.id, answerValue: answer },
                  ]);
                }}
                answerOptions={question.answerIDs}
                withCard={false}
              />
            ))}
          </>
        ) : (
          <></>
        )}
      </ContentContainer>
    );
  },
);
